/**
 * InstructionsModal Component
 * Pre-test instructions modal
 */
import React from 'react';

interface InstructionsModalProps {
  onClose: () => void;
  onStart: () => void;
  starting: boolean;
}

export const InstructionsModal: React.FC<InstructionsModalProps> = ({ 
  onClose, 
  onStart, 
  starting 
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl font-bold text-primary">Consigne de passation</h2>
        <button 
          onClick={onClose} 
          className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
        >
          &times;
        </button>
      </div>
      
      <div className="space-y-4 text-sm">
        {/* How to take the test */}
        <HowToTakeTest />

        {/* Before starting checklist */}
        <BeforeStartingChecklist />

        {/* Memory questions note */}
        <MemoryQuestionsNote />
      </div>

      {/* Action buttons */}
      <div className="mt-6 space-y-2">
        <button
          onClick={onStart}
          disabled={starting}
          className="w-full bg-secondary hover:bg-opacity-90 text-white font-bold py-3 rounded-lg text-base transition disabled:opacity-50"
        >
          {starting ? 'Lancement...' : 'Commencer maintenant'}
        </button>
        <button
          onClick={onClose}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 rounded-lg text-sm transition"
        >
          Annuler
        </button>
      </div>
    </div>
  </div>
);

// How to take the test section
const HowToTakeTest: React.FC = () => (
  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
    <h3 className="font-bold text-blue-900 mb-3">Comment répondre</h3>
    <div className="space-y-2">
      <InstructionItem text="Lisez bien chaque question" />
      <InstructionItem text="Choisissez une seule réponse par question" />
      <InstructionItem text="Gérez votre temps pour chaque dimension" />
      <InstructionItem text="Utilisez du papier pour vos calculs" />
    </div>
  </div>
);

const InstructionItem: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-2">
    <div className="text-blue-600 flex-shrink-0">•</div>
    <p className="text-blue-900 text-sm">{text}</p>
  </div>
);

// Before starting checklist
const BeforeStartingChecklist: React.FC = () => (
  <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
    <h3 className="font-bold text-green-900 mb-3">Avant de commencer</h3>
    <div className="space-y-2">
      <ChecklistItem text="Assurez-vous d'avoir 45 minutes sans interruption" />
      <ChecklistItem text="Préparez du papier et un stylo" />
      <ChecklistItem text="Installez-vous dans un endroit calme" />
      <ChecklistItem text="Éliminez les distractions" />
    </div>
  </div>
);

const ChecklistItem: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-2">
    <div className="text-green-600 flex-shrink-0">✓</div>
    <p className="text-green-900 text-sm">{text}</p>
  </div>
);

// Memory questions note
const MemoryQuestionsNote: React.FC = () => (
  <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500">
    <div className="flex items-start gap-2">
      <div className="text-amber-600 text-lg flex-shrink-0">ℹ️</div>
      <div>
        <h3 className="font-bold text-amber-900 mb-2">Note pour la dimension Mémorisation</h3>
        <div className="space-y-1.5 text-amber-900 text-sm">
          <p>1. Mémorisez l'information affichée (30-40 secondes)</p>
          <p>2. Organisez les couleurs selon la consigne (90-120 secondes)</p>
          <p>3. Répondez à la question posée</p>
        </div>
      </div>
    </div>
  </div>
);

export default InstructionsModal;